/**
 * 各品牌摄像头 RTSP 地址模板（纯函数 + 管理员可配置品牌注册表）。
 *
 * 数据分两层：
 *   1. 内置品牌表（下面 CAMERA_BRANDS / BUILTIN_TEMPLATES）——与代码同发布，
 *      其中 hikvision/dahua/uniview/tplink/jovision 与后端 jinglunGo(go2rtc.go
 *      buildRtspURL) 的 switch 一一对应，模板模式下由本地服务按品牌重拼地址。
 *   2. [2026-09-25] 字典配置品牌——后端管理员在 JEEG 数据字典里加两类条目，
 *      无需发版即可接入新品牌摄像头：
 *        · 字典 drying_camera_brand      item_value=品牌key  item_text=显示名（如 "乐橙"）
 *        · 字典 drying_camera_rtsp_tpl   item_value=品牌key  item_text=RTSP 模板串
 *      模板占位符（不区分大小写）：
 *        {user} 账号(已URL编码)  {pass} 密码(已URL编码)  {ip} 设备IP  {port} RTSP端口
 *        {ch} 通道号             {st}  码流(1主/2子)      {st1} 码流-1(0主/1子)
 *        {ch102} 通道号×100+码流(海康专用)  {sub} main/sub(中维专用)
 *      例(大华)：rtsp://{user}:{pass}@{ip}:{port}/cam/realmonitor?channel={ch}&subtype={st1}
 *      账号为空时 "{user}:{pass}@" / "{user}@" 自动省略。
 *      字典品牌在下发给 jinglunGo 时由前端把完整地址烘焙进通道 rtspUrl
 *      （见 go2rtc.js syncCameraConfigToGo2rtc），本地服务零改动即可取流。
 *
 * 前端用于：配置页/监控测试页的 RTSP 地址预览与生成（让用户肉眼核对拼出的地址对不对）。
 * 字典拉取失败时静默回落内置表（dictionaryCache 语义），页面永不白屏。
 */
import { loadDict } from './dictionaryCache'

// 品牌字典编码（JEEG sys_dict；两类条目按同一品牌 key 关联）
export const DICT_CODE_CAMERA_BRAND = 'drying_camera_brand'
export const DICT_CODE_CAMERA_RTSP_TPL = 'drying_camera_rtsp_tpl'

/**
 * 内置品牌表。key 与 jinglunGo go2rtc.go switch 分支同名的是"本地服务认识"的品牌；
 * xiongmai/tiandy/custom 只有前端模板（下发时前端烘焙完整地址）。
 */
export const CAMERA_BRANDS = [
  { key: 'hikvision', label: '海康威视' },
  { key: 'dahua', label: '大华' },
  { key: 'uniview', label: '宇视' },
  { key: 'tplink', label: 'TP-LINK' },
  { key: 'jovision', label: '中维世纪(Jovision)' },
  { key: 'xiongmai', label: '雄迈系白牌（赖田/乔安等）' },
  { key: 'tiandy', label: '天地伟业' },
  { key: 'custom', label: '自定义（填完整 RTSP）' }
]

// jinglunGo buildRtspURL 内置支持的品牌（这些走"模板模式"，通道可不锁地址由服务端重拼；
// 表外品牌一律由前端烘焙完整地址下发）
export const SERVICE_BUILTIN_BRANDS = ['hikvision', 'dahua', 'uniview', 'tplink', 'jovision', 'custom']

// 内置品牌 → RTSP 模板（与 jinglunGo go2rtc.go 逐条对齐；{st1}={st}-1）
const BUILTIN_TEMPLATES = {
  hikvision: 'rtsp://{user}:{pass}@{ip}:{port}/Streaming/Channels/{ch102}',
  dahua: 'rtsp://{user}:{pass}@{ip}:{port}/cam/realmonitor?channel={ch}&subtype={st1}',
  uniview: 'rtsp://{user}:{pass}@{ip}:{port}/unicast/c{ch}/s{st1}/live',
  tplink: 'rtsp://{user}:{pass}@{ip}:{port}/stream{ch}',
  jovision: 'rtsp://{user}:{pass}@{ip}:{port}/h264/ch{ch}/{sub}/av_stream',
  xiongmai: 'rtsp://{user}:{pass}@{ip}:{port}/user={user}&password={pass}&channel={ch}&stream={st1}.sdp?real_stream',
  tiandy: 'rtsp://{user}:{pass}@{ip}:{port}/{ch}/{st}'
}
// tiandy 的 /video1、/video2 只是码流简写且永远指向通道1，多通道 NVR 上会把
// 所有通道都烘成通道1的地址，必须用 /{ch}/{st}（通道/码流，1=主 2=子）
// {ch102} = 通道号×100+码流（海康专用），{sub} = main/sub（中维专用）
// ——这两个是内置表私有的复合占位符，字典模板里用基础占位符即可，但同样支持。

/** 品牌是否需要用户在通道里自填完整 RTSP 地址（custom 才需要）。 */
export function brandNeedsCustomUrl(brand) {
  return brand === 'custom'
}

// ================== 字典可配置品牌注册表 ==================

// brandKey → { label, template, fromDict }  模块级缓存，loadCameraBrandConfig() 填充
const brandRegistry = new Map()
let brandConfigLoaded = false

/**
 * 拉取字典品牌配置并合入注册表（内置表打底，字典同名覆盖 label/模板，新品牌追加）。
 * 并发调用只发一次请求；失败静默保持内置表。
 * @returns {Promise<Array<{key,label,fromDict}>>} 合并后的完整品牌列表
 */
export function loadCameraBrandConfig() {
  if (brandConfigLoaded) return Promise.resolve(getCameraBrands())
  return Promise.all([
    loadDict(DICT_CODE_CAMERA_BRAND),
    loadDict(DICT_CODE_CAMERA_RTSP_TPL)
  ]).then(([brandMap, tplMap]) => {
    brandRegistry.clear()
    // 字典模板/显示名按品牌 key 落注册表
    tplMap.forEach((tpl, key) => {
      if (key && String(tpl || '').trim()) {
        brandRegistry.set(key, { label: (brandMap.get(key) || key), template: String(tpl).trim(), fromDict: true })
      }
    })
    brandMap.forEach((label, key) => {
      if (!key) return
      const tpl = tplMap.get(key)
      if (tplMap.has(key) && String(tpl || '').trim()) return // 已在上面落过
      // 只有显示名没有模板：仅覆盖内置品牌的显示名（新品牌没模板没法拼地址，忽略）
      if (CAMERA_BRANDS.some(b => b.key === key)) {
        brandRegistry.set(key, { label: label, template: null, fromDict: false })
      }
    })
    brandConfigLoaded = true
    return getCameraBrands()
  })
}

/**
 * 当前生效的完整品牌列表（内置 + 字典追加；字典同名覆盖显示名）。
 * 同步函数：字典未加载完时只返回内置表，加载完成后调用方再刷新一次。
 */
export function getCameraBrands() {
  const list = CAMERA_BRANDS.map(b => {
    const reg = brandRegistry.get(b.key)
    return reg && reg.label ? { key: b.key, label: reg.label, fromDict: !!reg.fromDict } : { key: b.key, label: b.label, fromDict: false }
  })
  brandRegistry.forEach((reg, key) => {
    if (!CAMERA_BRANDS.some(b => b.key === key) && reg.template) {
      list.push({ key, label: reg.label || key, fromDict: true })
    }
  })
  // custom 固定排最后
  list.sort((a, b) => (a.key === 'custom' ? 1 : 0) - (b.key === 'custom' ? 1 : 0))
  return list
}

/**
 * 品牌的模板来源是否为字典配置（决定下发 go2rtc 时是否由前端烘焙完整地址：
 * 字典品牌 jinglunGo 不认识，必须烘焙；内置品牌保持模板模式由服务端重拼）。
 */
export function isDictConfiguredBrand(brand) {
  const reg = brandRegistry.get(String(brand || ''))
  return !!(reg && reg.fromDict && reg.template)
}

/**
 * 下发 jinglunGo 时该品牌的通道是否需要前端烘焙完整地址：
 * 服务内置品牌(hikvision/dahua/uniview/tplink/jovision)走模板模式由本地服务重拼；
 * 其余（字典配置品牌、雄迈/天地伟业等仅前端有模板的品牌）一律烘焙，
 * 保证本地服务零改动也能取到流。
 */
export function shouldBakeRtspUrl(brand) {
  const b = String(brand || 'hikvision')
  return isDictConfiguredBrand(b) || !SERVICE_BUILTIN_BRANDS.includes(b)
}

/** 模板插值：空账号先摘掉头部凭据段，再逐个替换占位符（值 URL 编码） */
function interpolateTemplate(tpl, opts) {
  const port = +opts.port > 0 ? +opts.port : 554
  const chID = +opts.channelId > 0 ? +opts.channelId : 1
  const st = +opts.streamType === 2 ? 2 : 1
  const user = opts.user != null ? String(opts.user) : ''
  const pass = opts.pass != null ? String(opts.pass) : ''
  let out = String(tpl)
  if (!user) {
    // 账号为空: 模板头部的 "{user}:{pass}@" / "{user}@" 凭据段整体省略
    // (xiongmai 式把账密放 query 的保留占位符, 插值成空值, 与旧实现一致)
    out = out.replace(/\{user\}\s*:\s*\{pass\}\s*@/gi, '').replace(/\{user\}\s*@/gi, '')
  }
  return out
    .replace(/\{user\}/gi, encodeURIComponent(user))
    .replace(/\{pass\}/gi, encodeURIComponent(pass))
    .replace(/\{ip\}/gi, String(opts.ip || ''))
    .replace(/\{port\}/gi, String(port))
    .replace(/\{ch102\}/gi, String(chID * 100 + st))
    .replace(/\{sub\}/gi, st === 2 ? 'sub' : 'main')
    .replace(/\{ch\}/gi, String(chID))
    .replace(/\{st1\}/gi, String(st - 1))
    .replace(/\{st\}/gi, String(st))
}

/**
 * 按品牌拼 RTSP 地址。
 * 优先级：custom 品牌自填完整地址 > 字典配置模板 > 内置品牌模板（与后端 buildRtspURL 同规则）。
 * @param {object} opts { brand, ip, port, user, pass, channelId, streamType, rtspUrl }
 * @returns {string} 完整 RTSP URL
 */
export function buildRtspURL(opts) {
  const brand = opts.brand || 'hikvision'
  // custom 品牌：优先用自填的完整地址
  if (brand === 'custom' && opts.rtspUrl && String(opts.rtspUrl).trim()) {
    return String(opts.rtspUrl).trim()
  }
  // 字典配置模板优先（管理员为内置品牌改了模板也以字典为准）
  const reg = brandRegistry.get(brand)
  if (reg && reg.template) {
    return interpolateTemplate(reg.template, opts)
  }
  const builtin = BUILTIN_TEMPLATES[brand] || BUILTIN_TEMPLATES.hikvision
  return interpolateTemplate(builtin, opts)
}
